/* FOM · Operating Brain na tela.

   A regra que governa cada decisao visual deste arquivo:

       O LEITOR PRECISA SABER DE ONDE VEIO CADA COISA.

   Dado do mapa e dado do mapa. Observacao e observacao. Recomendacao de agente
   e recomendacao de agente — e aparece marcada como tal, com o nome do agente,
   a justificativa e o que foi olhado para chegar ali. Nada do Brain e
   apresentado com a mesma voz de um fato do mapa.

   Por isso toda proposta mostra, junto e sem precisar de clique:

     · o que o agente sugere;
     · POR QUE (justificativa);
     · COM BASE EM QUE (observacoes — os fatos do mapa que ele leu);
     · o que a POLITICA decidiu, e o motivo;
     · em que estado a proposta esta.

   E por isso nao existe, em lugar nenhum, uma proposta renderizada sem a
   etiqueta de origem. */
import type { Api } from '../core/api.ts';
import { ApiError } from '../core/api.ts';
import type { BrainProposta, BrainStatus, BrainEvento, MemberRole } from '../core/types.ts';
import {
  ROTULO_PROPOSTA, ROTULO_POLITICA, ROTULO_STATUS_PROPOSTA,
} from '../core/types.ts';
import { el, botao, campo, area, limpar, vazio, dataCurta } from './dom.ts';

export type BrainOpcoes = {
  api: Api;
  org: string;
  mapa: string;
  papel: MemberRole;
  /* o mapa precisa se recompor quando uma aprovacao muta o dominio */
  aoAlterarDominio: () => Promise<void>;
  aoErro: (erro: unknown) => void;
};

type Aba = 'fila' | 'trilha';

export function montarBrain(host: HTMLElement, o: BrainOpcoes) {
  let status: BrainStatus | null = null;
  let propostas: BrainProposta[] = [];
  let eventos: BrainEvento[] = [];
  let aba: Aba = 'fila';
  let ocupado = false;
  let semAcesso = false;
  let ultimaExecucao: string | null = null;

  async function carregar(): Promise<void> {
    status = await o.api.brainStatus(o.org, o.mapa);
    propostas = status.granted ? await o.api.brainPropostas(o.org, o.mapa) : [];
    if (aba === 'trilha') eventos = await o.api.brainEventos(o.org, o.mapa);
  }

  async function comErro(fn: () => Promise<void>): Promise<void> {
    if (ocupado) return;
    ocupado = true;
    render();
    try {
      await fn();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) semAcesso = true;
      else o.aoErro(err);
    } finally {
      ocupado = false;
      render();
    }
  }

  /* ------------------------------------------------------------ cabecalho -- */

  function cabecalho(): void {
    const cab = el('div', 'gaveta-cabecalho');
    cab.appendChild(el('h3', 'gaveta-titulo', 'Operating Brain'));
    cab.appendChild(el('span', 'selo selo-agente', 'AGENTE'));
    cab.appendChild(el('span', 'selo selo-privado', 'PRIVADO'));
    host.appendChild(cab);

    /* A frase mais importante da tela. Sem ela, uma lista de sugestoes bem
       escritas passa a ser lida como se fosse o mapa falando. */
    host.appendChild(el('p', 'gaveta-nota',
      'O que aparece aqui e ANALISE, nao dado do mapa. O agente observa e propoe; '
      + 'nada vira conhecimento, classificacao ou publicacao sem uma pessoa decidir.'));
  }

  function painelStatus(): void {
    if (!status) return;

    if (!status.granted) {
      host.appendChild(vazio(
        'O agente nao esta habilitado nesta organizacao.',
        'Conceder autoridade a uma maquina e decisao de quem responde pela organizacao — '
        + 'e comeca sempre sem autonomia. Um owner precisa habilitar.',
      ));
      return;
    }

    const linha = el('div', 'brain-status');
    linha.appendChild(marca('autoridade efetiva', status.effective_role ?? '—',
      'o menor entre o seu papel e o teto concedido ao agente'));
    linha.appendChild(marca('camada privada', status.read_private ? 'le' : 'nao le',
      'ler investigacoes exige concessao explicita'));
    linha.appendChild(marca('autonomia', status.autonomous ? 'ligada' : 'desligada',
      status.autonomous
        ? 'acoes classificadas como seguras executam sozinhas'
        : 'toda acao espera uma pessoa aprovar'));
    linha.appendChild(marca('na fila', String(status.pending), 'propostas aguardando decisao'));
    host.appendChild(linha);

    if (status.last_run) {
      const r = status.last_run;
      const txt = r.status === 'failed'
        ? `ultima analise falhou (${r.error_code ?? 'sem codigo'}) — nenhuma proposta foi criada`
        : `ultima analise em ${dataCurta(r.finished_at ?? r.started_at)}`;
      host.appendChild(el('p', 'brain-ultima ' + (r.status === 'failed' ? 'brain-falhou' : ''), txt));
    }

    const acoes = el('div', 'formulario-acoes formulario-acoes-esq');
    const analisar = botao(
      ocupado ? 'analisando…' : 'Analisar agora',
      () => { void comErro(analisarAgora); },
      'botao botao-principal',
    );
    analisar.disabled = ocupado || o.papel === 'viewer';
    acoes.appendChild(analisar);
    host.appendChild(acoes);

    if (ultimaExecucao) host.appendChild(el('p', 'brain-eco', ultimaExecucao));
  }

  function marca(rotulo: string, valor: string, dica: string): HTMLElement {
    const d = el('div', 'brain-marca');
    d.appendChild(el('span', 'campo-rotulo', rotulo));
    d.appendChild(el('span', 'brain-marca-valor', valor));
    d.title = dica;
    return d;
  }

  async function analisarAgora(): Promise<void> {
    const r = await o.api.brainAnalisar(o.org, o.mapa);
    const obs = r.observation;
    /* O eco da execucao diz o que foi OLHADO. "Analisei" sem dizer o que foi
       lido e uma afirmacao que ninguem consegue conferir. */
    ultimaExecucao = r.status === 'failed'
      ? `A analise nao concluiu (${r.error_code}). Nada foi alterado.`
      : `Examinou ${obs.nos_examinados} de ${obs.nos_no_mapa} nos e `
        + `${obs.investigacoes_examinadas} investigacoes. `
        + `${r.propostas.length} proposta(s).`
        + (obs.privado_disponivel ? '' : ' A camada privada nao foi lida.');
    await carregar();
    /* execucao autonoma pode ter mutado o dominio */
    if (r.propostas.some((p) => p.status === 'executed')) await o.aoAlterarDominio();
  }

  /* ----------------------------------------------------------------- fila -- */

  function renderFila(): void {
    if (!status?.granted) return;

    const pendentes = propostas.filter((p) => p.status === 'pending');
    const decididas = propostas.filter((p) => p.status !== 'pending');

    host.appendChild(el('h4', 'gaveta-secao', 'Fila de trabalho · ' + String(pendentes.length)));
    if (!pendentes.length) {
      host.appendChild(vazio('Nada aguardando decisao.',
        'Rode uma analise para o agente olhar o mapa de novo.'));
    } else {
      for (const p of pendentes) host.appendChild(cartao(p, true));
    }

    if (decididas.length) {
      host.appendChild(el('h4', 'gaveta-secao', 'Ja decididas · ' + String(decididas.length)));
      for (const p of decididas) host.appendChild(cartao(p, false));
    }
  }

  function cartao(p: BrainProposta, decidivel: boolean): HTMLElement {
    const c = el('article', 'proposta proposta-' + p.status);

    const topo = el('div', 'proposta-topo');
    topo.appendChild(el('span', 'proposta-codigo', p.code));
    topo.appendChild(el('span', 'proposta-tipo', ROTULO_PROPOSTA[p.kind] ?? p.kind));
    topo.appendChild(el('span', 'proposta-risco proposta-risco-' + p.risk, 'risco ' + p.risk));
    c.appendChild(topo);

    c.appendChild(el('h5', 'proposta-titulo', p.title));

    /* POR QUE */
    const porque = el('div', 'proposta-bloco');
    porque.appendChild(el('span', 'campo-rotulo', 'Por que o agente sugere isto'));
    porque.appendChild(el('p', 'proposta-texto', p.rationale));
    c.appendChild(porque);

    /* COM BASE EM QUE — os fatos do mapa, separados da opiniao */
    if (p.observations.length) {
      const obs = el('div', 'proposta-bloco');
      obs.appendChild(el('span', 'campo-rotulo', 'O que ele leu no mapa'));
      const ul = el('ul', 'proposta-observacoes');
      for (const linha of p.observations) ul.appendChild(el('li', undefined, linha));
      obs.appendChild(ul);
      c.appendChild(obs);
    }

    if (p.gaps.length) {
      const g = el('div', 'proposta-bloco');
      g.appendChild(el('span', 'campo-rotulo', 'Lacunas envolvidas'));
      const ul = el('ul', 'proposta-lacunas');
      for (const linha of p.gaps) ul.appendChild(el('li', undefined, linha));
      g.appendChild(ul);
      c.appendChild(g);
    }

    if (p.target_ref) {
      c.appendChild(rodapeLinha('Alvo', `${p.target_kind}: ${p.target_ref}`));
    }

    /* O QUE A POLITICA DECIDIU — e por que. Uma proposta bloqueada explicada e
       informacao; bloqueada em silencio e so uma coisa que sumiu. */
    const pol = el('div', 'proposta-politica proposta-politica-' + p.policy_decision);
    pol.appendChild(el('span', 'campo-rotulo', 'Politica'));
    pol.appendChild(el('span', 'proposta-politica-valor', ROTULO_POLITICA[p.policy_decision]));
    pol.appendChild(el('span', 'campo-dica', p.policy_reason));
    c.appendChild(pol);

    /* ESTADO */
    const estado = el('div', 'proposta-estado');
    estado.appendChild(el('span', 'proposta-estado-valor', ROTULO_STATUS_PROPOSTA[p.status]));
    if (p.executed_at) {
      const feito = p.result && typeof p.result.investigation_code === 'string'
        ? ` — abriu ${String(p.result.investigation_code)}`
        : '';
      estado.appendChild(el('span', 'campo-dica', dataCurta(p.executed_at) + feito));
    } else if (p.decided_at) {
      estado.appendChild(el('span', 'campo-dica', dataCurta(p.decided_at)));
    }
    if (p.status === 'failed' && p.result && typeof p.result.error === 'string') {
      estado.appendChild(el('span', 'campo-dica', 'o dominio recusou: ' + String(p.result.error)));
    }
    if (p.decision_note) estado.appendChild(el('span', 'campo-dica', '"' + p.decision_note + '"'));
    c.appendChild(estado);

    if (decidivel && o.papel !== 'viewer') c.appendChild(decisao(p));
    return c;
  }

  function rodapeLinha(rotulo: string, valor: string): HTMLElement {
    const d = el('div', 'proposta-linha');
    d.appendChild(el('span', 'campo-rotulo', rotulo));
    d.appendChild(el('span', 'campo-valor', valor));
    return d;
  }

  function decisao(p: BrainProposta): HTMLElement {
    const bloco = el('div', 'proposta-decisao');
    const nota = area('', 2, 'por que voce esta decidindo assim (opcional)');
    bloco.appendChild(campo('Nota da decisao', nota));

    const acoes = el('div', 'formulario-acoes');
    acoes.appendChild(botao('Recusar', () => {
      void comErro(async () => {
        await o.api.brainRecusar(o.org, o.mapa, p.code, nota.value.trim() || undefined);
        await carregar();
      });
    }, 'botao'));

    if (p.executable) {
      const aprovar = botao('Aprovar e executar', () => {
        void comErro(async () => {
          await o.api.brainAprovar(o.org, o.mapa, p.code, nota.value.trim() || undefined);
          await carregar();
          /* aprovar muta o dominio de verdade: o mapa tem de acompanhar */
          await o.aoAlterarDominio();
        });
      }, 'botao botao-principal');
      aprovar.title = 'a acao passa pelas MESMAS regras de dominio de uma acao humana';
      acoes.appendChild(aprovar);
    } else {
      acoes.appendChild(el('span', 'campo-dica',
        'nao ha acao automatica: esta e uma leitura para uma pessoa agir'));
    }

    bloco.appendChild(acoes);
    return bloco;
  }

  /* --------------------------------------------------------------- trilha -- */

  function renderTrilha(): void {
    host.appendChild(el('h4', 'gaveta-secao', 'Trilha do agente'));
    host.appendChild(el('p', 'gaveta-nota',
      'Cada passo do agente e cada decisao humana sobre ele. So cresce.'));

    if (!eventos.length) {
      host.appendChild(vazio('Nenhuma atividade ainda.', 'O agente ainda nao rodou neste mapa.'));
      return;
    }
    const ul = el('ul', 'lista-trilha');
    for (const e of eventos) {
      const li = el('li', 'trilha-item trilha-' + e.actor_type);
      li.appendChild(el('span', 'trilha-data', dataCurta(e.at)));
      const meio = el('span', 'trilha-acao');
      meio.appendChild(el('b', 'trilha-evento', e.event));
      meio.appendChild(el('span', undefined, ' ' + e.detail));
      li.appendChild(meio);
      /* quem agiu — maquina ou gente — sempre visivel, nunca deduzido */
      li.appendChild(el('span', 'trilha-autor',
        e.actor_type === 'agent' ? (e.agent ?? 'agente') : 'pessoa'));
      ul.appendChild(li);
    }
    host.appendChild(ul);
  }

  /* --------------------------------------------------------------- render -- */

  function abas(): void {
    const barra = el('div', 'brain-abas');
    for (const [id, rotulo] of [['fila', 'Fila'], ['trilha', 'Trilha']] as const) {
      const b = botao(rotulo, () => {
        aba = id;
        void comErro(carregar);
      }, 'brain-aba' + (aba === id ? ' brain-aba-ativa' : ''));
      barra.appendChild(b);
    }
    host.appendChild(barra);
  }

  function render(): void {
    limpar(host);
    cabecalho();

    if (semAcesso) {
      host.appendChild(vazio(
        'Camada privada.',
        'Seu papel nesta organizacao nao acessa o agente. Quem decide isso e o servidor.',
      ));
      return;
    }

    painelStatus();
    if (!status?.granted) return;

    abas();
    if (aba === 'fila') renderFila();
    else renderTrilha();
  }

  return {
    async mostrar(): Promise<void> {
      semAcesso = false;
      try {
        await carregar();
        render();
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) { semAcesso = true; render(); return; }
        o.aoErro(err);
      }
    },
  };
}
